import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { onAnimationEnd } from '@theme/utilities';
import {
  StandardEvents,
  ProductSelectEvent,
  CartLinesUpdateEvent,
  CartErrorEvent
} from '@shopify/events';

/**
 * =========================================================
 * PAWJOURNEY — HELIO STICKY ADD TO CART
 * =========================================================
 *
 * Special logic works ONLY when .pj-pack exists.
 *
 * 1 PACK
 * Variant ID: 52904908521783
 *
 * 2 PACK
 * Variant ID: 52904908554551
 *
 * BUNDLE
 * Product handle: complete-pet-hair-set
 *
 * The sticky bar:
 * - changes title
 * - changes price
 * - changes image
 * - survives Helio morph()
 * - re-syncs after every variant change
 *
 * Other products keep normal Helio behavior.
 */


/* =========================================================
   TYPES
========================================================= */

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id]
 * @property {string} [title]
 * @property {string} [name]
 * @property {boolean} [available]
 * @property {number|string} [price]
 * @property {Object} [featured_image]
 * @property {Object} [featured_image.src]
 * @property {Object} [featured_media]
 * @property {Object} [featured_media.preview_image]
 */

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar
 * @property {HTMLButtonElement} addToCartButton
 * @property {HTMLElement} quantityDisplay
 * @property {HTMLElement} quantityNumber
 * @property {HTMLImageElement} productImage
 */


/* =========================================================
   COMPONENT
========================================================= */

class StickyAddToCartComponent extends Component {

  requiredRefs = [
    'stickyBar',
    'addToCartButton',
    'quantityDisplay',
    'quantityNumber'
  ];


  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {IntersectionObserver | null} */
  #mainBottomObserver = null;

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {boolean} */
  #hiddenByBottom = false;

  /**
   * Current PawJourney selection:
   *
   * '1'
   * '2'
   * 'bundle'
   * null
   *
   * @type {string|null}
   */
  #pjSelection = null;

  /**
   * PawJourney variant data
   */
  #pjVariants = {

    '1': {
      id: '52904908521783',
      title: '1 PACK',
      price: '$24.99'
    },

    '2': {
      id: '52904908554551',
      title: '2 PACK',
      price: '$39.99'
    }

  };

  /**
   * Bundle product
   */
  #pjBundle = {
    handle: 'complete-pet-hair-set',
    title: 'COMPLETE PET HAIR SET',
    price: '$34.99'
  };

  /**
   * Bundle variant ID
   *
   * Filled automatically from:
   * /products/complete-pet-hair-set.js
   *
   * @type {string|null}
   */
  #pjBundleVariantId = null;

  /**
   * Bundle image
   *
   * @type {string|null}
   */
  #pjBundleImage = null;

  /**
   * Pack images
   *
   * Loaded directly from Shopify product JSON.
   *
   * @type {{'1': string|null, '2': string|null}}
   */
  #pjPackImages = {
    '1': null,
    '2': null
  };


  /* =========================================================
     CONNECTED
  ========================================================= */

  connectedCallback() {

    super.connectedCallback();

    this.#setupIntersectionObserver();

    const { signal } = this.#abortController;

    const target = this.closest('.shopify-section');

    target?.addEventListener(
      StandardEvents.productSelect,
      this.#handleProductSelect,
      { signal }
    );

    document.addEventListener(
      StandardEvents.cartLinesUpdate,
      this.#handleCartAddComplete,
      { signal }
    );

    document.addEventListener(
      StandardEvents.cartError,
      this.#handleCartAddComplete,
      { signal }
    );

    document.addEventListener(
      ThemeEvents.quantitySelectorUpdate,
      this.#handleQuantityUpdate,
      { signal }
    );

    this.#getInitialQuantity();

    /*
     * Setup PawJourney only if .pj-pack exists.
     */
    this.#setupPawJourneyPackSync();

    /*
     * Shopify Chat
     */
    customElements.whenDefined('shopify-chat').then(() => {

      if (signal.aborted) return;

      if (
        this.#isStuck &&
        this.#isChatActive()
      ) {

        this.#hideStickyBar();

      }

    });

  }


  /* =========================================================
     DISCONNECTED
  ========================================================= */

  disconnectedCallback() {

    super.disconnectedCallback();

    this.#buyButtonsIntersectionObserver?.disconnect();

    this.#mainBottomObserver?.disconnect();

    this.#abortController.abort();

    if (this.#animationTimeout) {

      clearTimeout(
        this.#animationTimeout
      );

    }

  }


  /* =========================================================
     INTERSECTION OBSERVER
  ========================================================= */

  #setupIntersectionObserver() {

    const productForm =
      this.#getProductForm();

    if (!productForm) return;

    const buyButtonsBlock =
      productForm.closest('.buy-buttons-block');

    if (!buyButtonsBlock) return;

    const footer =
      document.querySelector('footer') ??
      document.querySelector('[class*="footer-group"]');

    if (!footer) return;


    /*
     * BUY BUTTON OBSERVER
     */

    this.#buyButtonsIntersectionObserver =
      new IntersectionObserver((entries) => {

        const [entry] = entries;

        if (!entry) return;


        /*
         * Show sticky
         */

        if (
          !entry.isIntersecting &&
          !this.#isStuck
        ) {

          const rect =
            entry.target.getBoundingClientRect();

          if (
            rect.bottom < 0 ||
            rect.top < 0
          ) {

            if (
              this.#isChatActive()
            ) return;

            this.#showStickyBar();

          }

        }


        /*
         * Hide sticky
         */

        else if (
          entry.isIntersecting &&
          this.#isStuck
        ) {

          this.#hiddenByBottom = false;

          this.#hideStickyBar();

        }

      });


    /*
     * FOOTER OBSERVER
     */

    this.#mainBottomObserver =
      new IntersectionObserver(
        (entries) => {

          const [entry] = entries;

          if (!entry) return;


          if (
            entry.isIntersecting &&
            this.#isStuck
          ) {

            this.#hiddenByBottom = true;

            this.#hideStickyBar();

          }

          else if (
            !entry.isIntersecting &&
            this.#hiddenByBottom
          ) {

            const rect =
              buyButtonsBlock.getBoundingClientRect();

            if (
              rect.bottom < 0 ||
              rect.top < 0
            ) {

              this.#hiddenByBottom = false;

              if (
                !this.#isChatActive()
              ) {

                this.#showStickyBar();

              }

            }

          }

        },
        {
          rootMargin:
            '200px 0px 0px 0px'
        }
      );


    this.#buyButtonsIntersectionObserver.observe(
      buyButtonsBlock
    );

    this.#mainBottomObserver.observe(
      footer
    );


    /*
     * Main Shopify Add To Cart button
     */

    this.#targetAddToCartButton =
      productForm.querySelector(
        '[ref="addToCartButton"]'
      );

  }


  /* =========================================================
     PAWJOURNEY SETUP
  ========================================================= */

  #setupPawJourneyPackSync() {

    const packBlock =
      document.querySelector('.pj-pack');

    /*
     * IMPORTANT:
     *
     * If there is no .pj-pack,
     * this is NOT the PawJourney product.
     */

    if (!packBlock) return;


    const packs =
      packBlock.querySelectorAll(
        '.pj-pack__pack'
      );

    const bundle =
      packBlock.querySelector(
        '#pjBundle'
      );


    if (
      !packs.length &&
      !bundle
    ) return;


    /*
     * Load product images / bundle.
     */

    this.#loadPawJourneyData();


    /*
     * PACK CLICK
     */

    packs.forEach((pack) => {

      pack.addEventListener(
        'click',
        () => {

          const packNumber =
            pack.dataset.pack;


          if (
            packNumber !== '1' &&
            packNumber !== '2'
          ) return;


          /*
           * Remove bundle
           */

          if (bundle) {

            bundle.classList.remove(
              'is-selected'
            );

          }


          /*
           * Remove other pack selection
           */

          packs.forEach((item) => {

            item.classList.remove(
              'is-selected'
            );

          });

          pack.classList.add(
            'is-selected'
          );


          /*
           * Store current selection
           */

          this.#pjSelection =
            packNumber;


          this.dataset.pjBundleSelected =
            'false';


          /*
           * Set actual Shopify variant
           */

          this.#setShopifyVariant(
            this.#pjVariants[packNumber].id
          );


          /*
           * Immediately update sticky
           */

          this.#applyPawJourneySticky(
            packNumber
          );


          /*
           * Small delayed re-sync.
           *
           * Helio may update DOM
           * immediately after click.
           */

          setTimeout(() => {

            this.#applyPawJourneySticky(
              packNumber
            );

          }, 50);


          setTimeout(() => {

            this.#applyPawJourneySticky(
              packNumber
            );

          }, 250);

        }
      );

    });


    /*
     * BUNDLE CLICK
     */

    if (bundle) {

      bundle.addEventListener(
        'click',
        () => {

          /*
           * Remove pack selection
           */

          packs.forEach((pack) => {

            pack.classList.remove(
              'is-selected'
            );

          });


          /*
           * Select bundle
           */

          bundle.classList.add(
            'is-selected'
          );


          /*
           * Store selection
           */

          this.#pjSelection =
            'bundle';


          this.dataset.pjBundleSelected =
            'true';


          /*
           * Make sure bundle variant
           * is loaded.
           */

          if (
            this.#pjBundleVariantId
          ) {

            this.#setShopifyVariant(
              this.#pjBundleVariantId
            );

          }


          /*
           * Update sticky immediately
           */

          this.#applyPawJourneySticky(
            'bundle'
          );


          /*
           * Re-apply after Helio updates.
           */

          setTimeout(() => {

            this.#applyPawJourneySticky(
              'bundle'
            );

          }, 50);


          setTimeout(() => {

            this.#applyPawJourneySticky(
              'bundle'
            );

          }, 250);

        }
      );

    }

  }


  /* =========================================================
     LOAD PAWJOURNEY DATA
  ========================================================= */

  async #loadPawJourneyData() {

    /*
     * Load main product JSON.
     */

    try {

      const product =
        await fetch(
          `${window.location.pathname}.js`
        )
          .then(
            response => response.ok
              ? response.json()
              : null
          );


      if (
        product &&
        product.variants
      ) {

        product.variants.forEach(
          (variant) => {

            const id =
              String(
                variant.id
              );


            /*
             * 1 PACK
             */

            if (
              id ===
              this.#pjVariants['1'].id
            ) {

              const image =
                this.#getVariantImage(
                  variant
                );

              if (image) {

                this.#pjPackImages['1'] =
                  image;

              }

            }


            /*
             * 2 PACK
             */

            if (
              id ===
              this.#pjVariants['2'].id
            ) {

              const image =
                this.#getVariantImage(
                  variant
                );

              if (image) {

                this.#pjPackImages['2'] =
                  image;

              }

            }

          }
        );

      }

    }
    catch (error) {

      console.warn(
        '[PawJourney] Main product data error:',
        error
      );

    }


    /*
     * Load Bundle product JSON.
     */

    try {

      const bundleProduct =
        await fetch(
          '/products/complete-pet-hair-set.js'
        )
          .then(
            response => response.ok
              ? response.json()
              : null
          );


      if (
        bundleProduct &&
        bundleProduct.variants &&
        bundleProduct.variants.length
      ) {

        /*
         * Bundle variant
         */

        const bundleVariant =
          bundleProduct.variants[0];


        this.#pjBundleVariantId =
          String(
            bundleVariant.id
          );


        /*
         * Bundle image
         */

        this.#pjBundleImage =
          this.#getVariantImage(
            bundleVariant
          );


        /*
         * If no variant image,
         * use product featured image.
         */

        if (
          !this.#pjBundleImage &&
          bundleProduct.featured_image
        ) {

          this.#pjBundleImage =
            this.#getImageSrc(
              bundleProduct.featured_image
            );

        }

      }

    }
    catch (error) {

      console.warn(
        '[PawJourney] Bundle data error:',
        error
      );

    }


    /*
     * Re-sync after data loaded.
     */

    if (this.#pjSelection) {

      this.#applyPawJourneySticky(
        this.#pjSelection
      );

    }

  }


  /* =========================================================
     GET VARIANT IMAGE
  ========================================================= */

  #getVariantImage(variant) {

    if (!variant) return null;


    /*
     * Shopify product JSON can expose
     * featured_image.
     */

    if (
      variant.featured_image
    ) {

      const image =
        this.#getImageSrc(
          variant.featured_image
        );

      if (image) return image;

    }


    /*
     * Some JSON uses image.
     */

    if (
      variant.image
    ) {

      const image =
        this.#getImageSrc(
          variant.image
        );

      if (image) return image;

    }


    /*
     * Some theme responses use
     * featured_media.preview_image.
     */

    if (
      variant.featured_media &&
      variant.featured_media.preview_image
    ) {

      const image =
        this.#getImageSrc(
          variant.featured_media.preview_image
        );

      if (image) return image;

    }


    return null;

  }


  /* =========================================================
     GET IMAGE SRC
  ========================================================= */

  #getImageSrc(image) {

    if (!image) return null;


    if (
      typeof image === 'string'
    ) {

      return image;

    }


    if (
      image.src
    ) {

      if (
        typeof image.src === 'string'
      ) {

        return image.src;

      }

    }


    return null;

  }


  /* =========================================================
     SET SHOPIFY VARIANT
  ========================================================= */

  #setShopifyVariant(variantId) {

    const productForm =
      this.#getProductForm();

    if (!productForm) return;


    let variantInput =
      productForm.querySelector(
        'input[name="id"]'
      );


    if (!variantInput) {

      variantInput =
        document.createElement(
          'input'
        );

      variantInput.type =
        'hidden';

      variantInput.name =
        'id';

      productForm.appendChild(
        variantInput
      );

    }


    variantInput.value =
      String(variantId);


    /*
     * Bundle marker
     */

    if (
      this.#pjSelection === 'bundle'
    ) {

      variantInput.setAttribute(
        'data-pj-bundle',
        'true'
      );

    }

    else {

      variantInput.removeAttribute(
        'data-pj-bundle'
      );

    }


    /*
     * Keep component dataset
     * synchronized.
     */

    this.dataset.currentVariantId =
      String(variantId);

  }


  /* =========================================================
     APPLY PAWJOURNEY STICKY
  ========================================================= */

  #applyPawJourneySticky(selection) {

    const variantTitle =
      this.querySelector(
        '.sticky-add-to-cart__variant'
      );


    const productImage =
      this.refs.productImage;


    /*
     * =====================================================
     * 1 PACK
     * =====================================================
     */

    if (
      selection === '1'
    ) {

      const data =
        this.#pjVariants['1'];


      if (variantTitle) {

        variantTitle.textContent =
          data.title;

      }


      /*
       * Price
       */

      this.#setStickyPrice(
        data.price
      );


      /*
       * Image
       */

      if (
        productImage &&
        this.#pjPackImages['1']
      ) {

        this.#setStickyImage(
          this.#pjPackImages['1'],
          productImage,
          data.title
        );

      }


      this.dataset.pjBundleSelected =
        'false';


      return;

    }


    /*
     * =====================================================
     * 2 PACK
     * =====================================================
     */

    if (
      selection === '2'
    ) {

      const data =
        this.#pjVariants['2'];


      if (variantTitle) {

        variantTitle.textContent =
          data.title;

      }


      /*
       * Price
       */

      this.#setStickyPrice(
        data.price
      );


      /*
       * Image
       */

      if (
        productImage &&
        this.#pjPackImages['2']
      ) {

        this.#setStickyImage(
          this.#pjPackImages['2'],
          productImage,
          data.title
        );

      }


      this.dataset.pjBundleSelected =
        'false';


      return;

    }


    /*
     * =====================================================
     * BUNDLE
     * =====================================================
     */

    if (
      selection === 'bundle'
    ) {

      if (variantTitle) {

        variantTitle.textContent =
          this.#pjBundle.title;

      }


      /*
       * Bundle price
       */

      this.#setStickyPrice(
        this.#pjBundle.price
      );


      /*
       * Bundle image
       */

      if (
        productImage &&
        this.#pjBundleImage
      ) {

        this.#setStickyImage(
          this.#pjBundleImage,
          productImage,
          this.#pjBundle.title
        );

      }


      this.dataset.pjBundleSelected =
        'true';

    }

  }


  /* =========================================================
     SET STICKY IMAGE
  ========================================================= */

  #setStickyImage(
    imageUrl,
    imageElement,
    altText
  ) {

    if (
      !imageUrl ||
      !imageElement
    ) return;


    /*
     * Shopify CDN image URL
     *
     * Remove old width parameters
     * so the sticky can request its own size.
     */

    let finalUrl =
      String(imageUrl);


    /*
     * Make sure URL is usable.
     */

    if (
      finalUrl.startsWith('//')
    ) {

      finalUrl =
        'https:' +
        finalUrl;

    }


    /*
     * Prevent Helio morph from leaving
     * the previous image cached.
     */

    imageElement.src =
      finalUrl;


    imageElement.setAttribute(
      'src',
      finalUrl
    );


    /*
     * srcset can otherwise cause browser
     * to continue showing the old image.
     */

    imageElement.removeAttribute(
      'srcset'
    );


    imageElement.removeAttribute(
      'sizes'
    );


    if (altText) {

      imageElement.alt =
        altText;

    }


    /*
     * Force browser to decode
     * the new image.
     */

    if (
      typeof imageElement.decode ===
      'function'
    ) {

      imageElement.decode()
        .catch(() => {});

    }

  }


  /* =========================================================
     SET STICKY PRICE
  ========================================================= */

  #setStickyPrice(price) {

    /*
     * Helio can use several price selectors.
     *
     * We update only elements inside
     * sticky-add-to-cart.
     */

    const selectors = [
      '.sticky-add-to-cart__price',
      '[ref="price"]',
      '.price',
      '.money'
    ];


    let priceElements = [];


    selectors.forEach(
      (selector) => {

        this.querySelectorAll(
          selector
        ).forEach(
          (element) => {

            if (
              !priceElements.includes(
                element
              )
            ) {

              priceElements.push(
                element
              );

            }

          }
        );

      }
    );


    /*
     * If no price element found,
     * do nothing.
     */

    if (
      !priceElements.length
    ) return;


    priceElements.forEach(
      (element) => {

        element.textContent =
          price;

      }
    );

  }


  /* =========================================================
     ADD TO CART
  ========================================================= */

  handleAddToCartClick =
    async () => {

      if (
        !this.#targetAddToCartButton
      ) return;


      /*
       * IMPORTANT:
       *
       * Before clicking Shopify's real
       * Add To Cart button, ensure the
       * correct PawJourney variant is
       * inside the product form.
       */

      if (
        document.querySelector(
          '.pj-pack'
        )
      ) {

        if (
          this.#pjSelection === '1'
        ) {

          this.#setShopifyVariant(
            this.#pjVariants['1'].id
          );

        }

        else if (
          this.#pjSelection === '2'
        ) {

          this.#setShopifyVariant(
            this.#pjVariants['2'].id
          );

        }

        else if (
          this.#pjSelection === 'bundle' &&
          this.#pjBundleVariantId
        ) {

          this.#setShopifyVariant(
            this.#pjBundleVariantId
          );

        }

      }


      /*
       * Puppet flag
       */

      this.#targetAddToCartButton.dataset.puppet =
        'true';


      /*
       * Click REAL Shopify button.
       */

      this.#targetAddToCartButton.click();


      const cartIcon =
        document.querySelector(
          '.header-actions__cart-icon'
        );


      if (
        this.refs.addToCartButton
      ) {

        this.refs.addToCartButton.dataset.added =
          'true';

      }


      if (
        !cartIcon ||
        !this.refs.addToCartButton ||
        !this.refs.productImage
      ) return;


      if (this.#resetTimeout) {

        clearTimeout(
          this.#resetTimeout
        );

      }


      const flyToCartElement =
        /** @type {FlyToCart} */
        (
          document.createElement(
            'fly-to-cart'
          )
        );


      flyToCartElement.classList.add(
        'fly-to-cart--sticky'
      );


      flyToCartElement.style.setProperty(
        'background-image',
        `url(${this.refs.productImage.src})`
      );


      flyToCartElement.useSourceSize =
        'true';


      flyToCartElement.source =
        this.refs.productImage;


      flyToCartElement.destination =
        cartIcon;


      document.body.appendChild(
        flyToCartElement
      );


      await onAnimationEnd([
        this.refs.addToCartButton,
        flyToCartElement
      ]);


      this.#resetTimeout =
        setTimeout(() => {

          this.refs.addToCartButton
            .removeAttribute(
              'data-added'
            );

        }, 800);

    };


  /* =========================================================
     PRODUCT SELECT
  ========================================================= */

  #handleProductSelect = (event) => {

    if (
      !(event.target instanceof Element) ||
      event.target.closest('product-card')
    ) return;


    const isPawJourneyPack =
      Boolean(
        document.querySelector(
          '.pj-pack'
        )
      );


    const { optionValueId } =
      event.detail ?? {};


    if (optionValueId) {

      this.dataset.currentVariantId =
        optionValueId;

    }


    event.promise
      .then(({ detail }) => {

        if (!detail?.html) return;


        const {
          html,
          productId,
          resource: variant
        } = detail;


        if (
          productId &&
          productId !== this.dataset.productId
        ) return;


        const newStickyAddToCart =
          /** @type {HTMLElement|null} */
          (
            html.querySelector(
              'sticky-add-to-cart'
            )
          );


        if (!newStickyAddToCart) return;


        const newStickyBar =
          newStickyAddToCart.querySelector(
            '[ref="stickyBar"]'
          );


        if (!newStickyBar) return;


        const currentStuck =
          this.refs.stickyBar.getAttribute(
            'data-stuck'
          ) || 'false';


        const variantAvailable =
          newStickyAddToCart.dataset
            .variantAvailable;


        /*
         * Helio morph
         */

        morph(
          this.refs.stickyBar,
          newStickyBar,
          {
            childrenOnly: true
          }
        );


        /*
         * Restore sticky visibility
         */

        this.refs.stickyBar.setAttribute(
          'data-stuck',
          currentStuck
        );


        this.dataset.variantAvailable =
          variantAvailable;


        /*
         * Normal variant ID
         */

        if (
          variant &&
          variant.id
        ) {

          this.dataset.currentVariantId =
            variant.id;

        }


        /*
         * Re-cache Shopify button
         */

        const productForm =
          this.#getProductForm();


        if (productForm) {

          this.#targetAddToCartButton =
            productForm.querySelector(
              '[ref="addToCartButton"]'
            );

        }


        /*
         * Normal Helio behavior
         */

        if (
          variant == null
        ) {

          this.#handleVariantUnavailable();

        }


        this.#updateButtonText();


        /*
         * =================================================
         * PAWJOURNEY RE-SYNC
         *
         * This is the important fix.
         *
         * Helio morph() can replace the image,
         * title and price.
         *
         * We immediately put back the correct
         * PawJourney selection.
         * =================================================
         */

        if (
          isPawJourneyPack &&
          this.#pjSelection
        ) {

          /*
           * Apply immediately
           */

          this.#applyPawJourneySticky(
            this.#pjSelection
          );


          /*
           * Apply again after DOM settles
           */

          setTimeout(() => {

            this.#applyPawJourneySticky(
              this.#pjSelection
            );

          }, 50);


          /*
           * One more time because Helio
           * may finish morphing asynchronously.
           */

          setTimeout(() => {

            this.#applyPawJourneySticky(
              this.#pjSelection
            );

          }, 250);

        }

      })
      .catch((error) => {

        if (
          error?.name !== 'AbortError'
        ) {

          console.warn(
            '[sticky-add-to-cart] Event promise rejected:',
            error
          );

        }

      });

  };


  /* =========================================================
     CART ADD COMPLETE
  ========================================================= */

  #handleCartAddComplete = (event) => {

    const resetPuppet = () => {

      if (
        this.#targetAddToCartButton
      ) {

        this.#targetAddToCartButton.dataset.puppet =
          'false';

      }

    };


    if (
      'promise' in event &&
      event.promise instanceof Promise
    ) {

      event.promise.finally(
        resetPuppet
      );

    }

    else {

      resetPuppet();

    }

  };


  /* =========================================================
     QUANTITY
  ========================================================= */

  #handleQuantityUpdate = (event) => {

    if (
      event.detail.cartLine
    ) return;


    this.#currentQuantity =
      event.detail.quantity;


    this.#updateButtonText();

  };


  /* =========================================================
     SHOW STICKY
  ========================================================= */

  #showStickyBar() {

    const { stickyBar } =
      this.refs;


    this.#isStuck =
      true;


    stickyBar.dataset.stuck =
      'true';

  };


  /* =========================================================
     HIDE STICKY
  ========================================================= */

  #hideStickyBar() {

    const { stickyBar } =
      this.refs;


    this.#isStuck =
      false;


    stickyBar.dataset.stuck =
      'false';

  };


  /* =========================================================
     CHAT
  ========================================================= */

  #isChatActive() {

    if (
      !customElements.get(
        'shopify-chat'
      )
    ) {

      return false;

    }


    return Boolean(
      document.querySelector(
        'shopify-chat'
      )
    );

  };


  /* =========================================================
     PRODUCT FORM
  ========================================================= */

  #getProductForm() {

    const productId =
      this.dataset.productId;


    if (!productId) return null;


    const sectionElement =
      this.closest(
        '.shopify-section'
      );


    if (!sectionElement) return null;


    const sectionId =
      sectionElement.id.replace(
        'shopify-section-',
        ''
      );


    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );

  };


  /* =========================================================
     INITIAL QUANTITY
  ========================================================= */

  #getInitialQuantity() {

    this.#currentQuantity =
      parseInt(
        this.dataset.initialQuantity ||
        '1'
      ) || 1;


    this.#updateButtonText();

  };


  /* =========================================================
     BUTTON TEXT
  ========================================================= */

  #updateButtonText() {

    const {
      addToCartButton,
      quantityDisplay,
      quantityNumber
    } = this.refs;


    const available =
      !addToCartButton.disabled;


    quantityNumber.textContent =
      this.#currentQuantity.toString();


    if (
      available &&
      this.#currentQuantity > 1
    ) {

      quantityDisplay.style.display =
        'inline';

    }

    else {

      quantityDisplay.style.display =
        'none';

    }

  };


  /* =========================================================
     VARIANT UNAVAILABLE
  ========================================================= */

  #handleVariantUnavailable = () => {

    this.dataset.currentVariantId =
      '';


    const variantTitleElement =
      this.querySelector(
        '.sticky-add-to-cart__variant'
      );


    const productId =
      this.dataset.productId;


    const variantPicker =
      document.querySelector(
        `variant-picker[data-product-id="${productId}"]`
      );


    if (
      !variantTitleElement ||
      !variantPicker
    ) return;


    const selectedOptions =
      Array.from(
        variantPicker.querySelectorAll(
          'input:checked'
        )
      )
        .map(
          (option) =>
            /** @type {HTMLInputElement} */
            (option).value
        )
        .filter(
          (value) =>
            value !== ''
        )
        .join(' / ');


    if (!selectedOptions) return;


    variantTitleElement.textContent =
      selectedOptions;

  };

}


/* =========================================================
   DEFINE CUSTOM ELEMENT
========================================================= */

if (
  !customElements.get(
    'sticky-add-to-cart'
  )
) {

  customElements.define(
    'sticky-add-to-cart',
    StickyAddToCartComponent
  );

}